import { css } from 'styled-components';
import { ButtonProps, Theme } from '@mui/material';
import { getTextColoredIconRules } from '../../style/iconColors';
import { componentToken, declaration, pressed } from '../../style/utils';
import { ButtonSize } from './types';

type StyleProps = ButtonProps & { $size?: ButtonSize };

type ButtonTokens = Theme['tokens']['components']['button'];

const sizeKey = ($size?: ButtonSize) =>
  $size === 'extraSmall' || $size === 'large' ? $size : 'medium';

// For gray tile buttons that draw their own `background` and
// `hoverBackground`.
export const tileStyles = (
  theme: Theme,
  {
    background,
    hoverBackground,
    icon,
  }: { background: string; hoverBackground: string; icon?: string },
) => {
  const { tile } = theme.tokens.components.button;
  if (!tile.icon) return '';
  return `
    svg path { fill: ${icon || tile.icon}; }
    ${pressed(hoverBackground)}
    :disabled {
      background: ${background};
      svg path { fill: ${tile.disabledIcon}; }
    }
  `;
};

export const borderlessRules = (
  colors: ButtonTokens['contained']['secondary'],
) => `
  background: ${colors.bg};
  border: none;
  color: ${colors.fg};
  :hover {
    background: ${colors.hoverBg};
    color: ${colors.hoverFg};
  }
  ${pressed(colors.pressedBg)}
  :disabled {
    background: ${colors.disabledBg};
    color: ${colors.disabledFg};
    svg > path {
      ${declaration('fill', colors.disabledIcon)}
    }
  }
`;

const outlinedRules = (colors: ButtonTokens['outlined']['primary']) => `
  background: ${colors.bg};
  border-color: ${colors.border};
  color: ${colors.fg};
  :hover {
    background: ${colors.hoverBg};
    border-color: ${colors.hoverBorder};
    color: ${colors.hoverFg};
  }
  ${pressed(colors.pressedBg)}
  :disabled {
    ${declaration('background', colors.disabledBg)}
    ${declaration('color', colors.disabledFg)}
    ${declaration('border-color', colors.disabledBorder)}
    svg > path {
      ${declaration('fill', colors.disabledIcon)}
    }
  }
`;

const buttonStyles = css<StyleProps>`
  text-transform: none;
  box-shadow: none;
  font-weight: ${({ theme, $size }) =>
    theme.tokens.components.button.size[sizeKey($size)].fontWeight};
  font-size: ${({ theme, $size }) =>
    theme.tokens.components.button.size[sizeKey($size)].fontSize};
  height: ${({ theme, $size }) =>
    theme.tokens.components.button.size[sizeKey($size)].height};
  padding: ${({ theme, $size }) =>
    theme.tokens.components.button.size[sizeKey($size)].padding};
  border-radius: ${componentToken('button.radius')};
  white-space: nowrap;

  &:hover {
    box-shadow: none;
  }

  :disabled {
    svg > path {
      fill: ${componentToken('button.disabledIcon')};
    }
  }

  ${({ theme, variant, color }) => {
    const { contained, outlined, text } = theme.tokens.components.button;
    if (variant === 'contained') {
      if (color === 'primary') {
        return `
          background: ${contained.primary.bg};
          color: ${contained.primary.fg};
          border: none;
          ${pressed(contained.primary.pressedBg)}
          :disabled {
            background: ${contained.primary.disabledBg};
            color:  ${contained.primary.disabledFg};
            svg > path {
              fill: ${contained.primary.disabledIcon};
            }
          }
        `;
      }
      if (color === 'secondary') {
        return borderlessRules(contained.secondary);
      }
      if (color === 'error') {
        return `
          background: ${contained.error.bg};
          border: none;
          color: ${contained.error.fg};
          :hover {
            background: ${contained.error.hoverBg};
            filter: ${contained.error.hoverFilter};
          }
        `;
      }
    }

    if (variant === 'outlined') {
      if (color === 'primary') {
        return outlinedRules(outlined.primary);
      }
      if (color === 'secondary') {
        return outlinedRules(outlined.secondary);
      }
    }

    if (variant === 'text') {
      if (color === 'primary') {
        return `
          background: none;
          border: none;
          :hover {
            background: ${text.primary.hoverBg};
            ${declaration('color', text.primary.hoverFg)}
          }
          ${pressed(text.primary.pressedBg)}
      `;
      }
      if (color === 'secondary') {
        return `
          background: none;
          border: none;
          color: ${text.secondary.fg};
          :hover {
            background: ${text.secondary.hoverBg};
            color: ${text.secondary.hoverFg};
          }
          ${pressed(text.secondary.pressedBg)}
          :disabled {
            ${declaration('color', text.secondary.disabledFg)}
            svg > path {
              ${declaration('fill', text.secondary.disabledIcon)}
            }
          }
        `;
      }
    }

    return ``;
  }}

  ${({ theme }) =>
    theme.tokens.components.button.labelColoredIcons &&
    getTextColoredIconRules('&', theme)}
`;

export default buttonStyles;
